import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  attendanceAdjustmentsApiErrorMessage,
  createAttendanceAdjustments,
  deleteAttendanceAdjustment,
  formatAdjustmentMinutes,
  listAttendanceAdjustments,
  type AttendanceAdjustmentRow,
  type AttendanceAdjustmentType,
} from "@/lib/hr/attendanceAdjustmentsStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const HOUR_OPTIONS = Array.from({ length: 13 }, (_, i) => i); // 0–12
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i); // 0–59

type TimeOption = { value: string; label: string; search: string };

type DraftLine = {
  clientId: string;
  adjustmentType: AttendanceAdjustmentType;
  hoursPart: string;
  minutesPart: string;
  reason: string;
};

function newLine(): DraftLine {
  return {
    clientId: `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    adjustmentType: "add",
    hoursPart: "0",
    minutesPart: "0",
    reason: "",
  };
}

function SearchableTimePartPicker({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: TimeOption[];
  placeholder: string;
  searchPlaceholder: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [cycle, setCycle] = useState(0);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (next) setCycle((c) => c + 1);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "glass-input h-10 w-full justify-between px-3 font-normal",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        sideOffset={4}
      >
        <Command key={cycle} shouldFilter>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList className="max-h-56">
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = value === opt.value;
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.search}
                    onSelect={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-3.5 w-3.5 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    {opt.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export type AttendanceAdjustmentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  employeeName: string | null;
  adjustmentDate: string;
  canCreate: boolean;
  canDelete: boolean;
};

export function AttendanceAdjustmentDialog({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  adjustmentDate,
  canCreate,
  canDelete,
}: AttendanceAdjustmentDialogProps) {
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [existing, setExisting] = useState<AttendanceAdjustmentRow[]>([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const hourOptions = useMemo<TimeOption[]>(
    () =>
      HOUR_OPTIONS.map((h) => ({
        value: String(h),
        label: `${h}h`,
        search: `${h} ${h}h hours hour`,
      })),
    [],
  );
  const minuteOptions = useMemo<TimeOption[]>(
    () =>
      MINUTE_OPTIONS.map((m) => ({
        value: String(m),
        label: `${m}m`,
        search: `${m} ${m}m minutes minute`,
      })),
    [],
  );

  const loadExisting = useCallback(async () => {
    if (!employeeId || !adjustmentDate) return;
    setLoadingExisting(true);
    try {
      const rows = await listAttendanceAdjustments({ employeeId, adjustmentDate });
      setExisting(rows);
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
      setExisting([]);
    } finally {
      setLoadingExisting(false);
    }
  }, [employeeId, adjustmentDate]);

  useEffect(() => {
    if (!open) return;
    setLines([newLine()]);
    void loadExisting();
  }, [open, loadExisting]);

  const close = () => {
    if (saving || deletingId) return;
    onOpenChange(false);
  };

  const updateLine = (clientId: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((l) => (l.clientId === clientId ? { ...l, ...patch } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, newLine()]);

  const removeLine = (clientId: string) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.clientId !== clientId)));
  };

  const save = async () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create attendance adjustments.");
      return;
    }

    const payloadLines: Array<{
      adjustmentType: AttendanceAdjustmentType;
      minutes: number;
      reason?: string | null;
    }> = [];

    for (const [index, line] of lines.entries()) {
      const hoursPart = Number(line.hoursPart);
      const minutesPart = Number(line.minutesPart);
      if (
        !Number.isInteger(hoursPart) ||
        hoursPart < 0 ||
        hoursPart > 12 ||
        !Number.isInteger(minutesPart) ||
        minutesPart < 0 ||
        minutesPart > 59
      ) {
        toast.error(`Line ${index + 1}: select valid hours (0–12) and minutes (0–59).`);
        return;
      }
      const totalMinutes = hoursPart * 60 + minutesPart;
      if (totalMinutes < 1) {
        toast.error(`Line ${index + 1}: select at least 1 minute.`);
        return;
      }
      payloadLines.push({
        adjustmentType: line.adjustmentType,
        minutes: totalMinutes,
        reason: line.reason.trim() || null,
      });
    }

    setSaving(true);
    try {
      const result = await createAttendanceAdjustments({
        employeeId,
        adjustmentDate,
        lines: payloadLines,
      });
      toast.success(result.message);
      setLines([newLine()]);
      await loadExisting();
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const removeExisting = async (id: string) => {
    if (!canDelete) {
      toast.error("You don’t have permission to delete attendance adjustments.");
      return;
    }
    setDeletingId(id);
    try {
      const result = await deleteAttendanceAdjustment(id);
      toast.success(result.message);
      setExisting((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(attendanceAdjustmentsApiErrorMessage(e));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-5xl w-[95vw]">
        <DialogHeader>
          <DialogTitle>Attendance adjustment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <Input className="glass-input h-9" value={employeeName?.trim() || "—"} disabled readOnly />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input className="glass-input h-9" value={adjustmentDate} disabled readOnly />
            </div>
          </div>

          {canCreate ? (
            <div className="space-y-3">
              <div className="hidden md:grid gap-3 px-1 md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(6rem,0.7fr)_minmax(6rem,0.7fr)_minmax(12rem,1.4fr)_auto]">
                <Label className="text-xs text-muted-foreground">Type</Label>
                <Label className="text-xs text-muted-foreground">Hours</Label>
                <Label className="text-xs text-muted-foreground">Minutes</Label>
                <Label className="text-xs text-muted-foreground">Reason</Label>
                <span className="sr-only">Actions</span>
              </div>

              {lines.map((line, index) => (
                <div
                  key={line.clientId}
                  className="grid grid-cols-1 gap-3 items-end rounded-lg border border-border/60 p-3 md:border-0 md:p-0 md:rounded-none md:grid-cols-[minmax(7.5rem,0.85fr)_minmax(6rem,0.7fr)_minmax(6rem,0.7fr)_minmax(12rem,1.4fr)_auto]"
                >
                  <div className="space-y-2">
                    {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Type</Label> : null}
                    <Select
                      value={line.adjustmentType}
                      onValueChange={(v) => updateLine(line.clientId, { adjustmentType: v as AttendanceAdjustmentType })}
                    >
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="add">Add</SelectItem>
                        <SelectItem value="deduct">Deduct</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Hours</Label> : null}
                    <SearchableTimePartPicker
                      value={line.hoursPart}
                      onChange={(v) => updateLine(line.clientId, { hoursPart: v })}
                      options={hourOptions}
                      placeholder="Hours"
                      searchPlaceholder="Search hours…"
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-2">
                    {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Minutes</Label> : null}
                    <SearchableTimePartPicker
                      value={line.minutesPart}
                      onChange={(v) => updateLine(line.clientId, { minutesPart: v })}
                      options={minuteOptions}
                      placeholder="Minutes"
                      searchPlaceholder="Search minutes…"
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-2">
                    {index === 0 ? <Label className="text-xs text-muted-foreground md:sr-only">Reason</Label> : null}
                    <Input
                      className="glass-input h-10"
                      value={line.reason}
                      onChange={(e) => updateLine(line.clientId, { reason: e.target.value })}
                      placeholder="Optional reason"
                      maxLength={255}
                      disabled={saving}
                    />
                  </div>
                  <div className="flex items-center gap-2 pb-0.5">
                    {index === lines.length - 1 ? (
                      <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={addLine} aria-label="Add another line">
                        <Plus className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0 text-destructive"
                        onClick={() => removeLine(line.clientId)}
                        aria-label="Remove line"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Existing adjustments</Label>
            {loadingExisting ? (
              <div className="rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading adjustments…
                </span>
              </div>
            ) : existing.length === 0 ? (
              <p className="text-sm text-muted-foreground">No adjustments for this employee on this date.</p>
            ) : (
              <div className="rounded-lg border border-border/60 divide-y divide-border/50 overflow-hidden">
                {existing.map((row) => (
                  <div key={row.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium capitalize">
                        {row.adjustmentType === "deduct" ? "Deduct" : "Add"} · {formatAdjustmentMinutes(row.minutes)}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{row.reason?.trim() || "No reason"}</p>
                    </div>
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive shrink-0"
                        disabled={deletingId === row.id}
                        onClick={() => void removeExisting(row.id)}
                        aria-label="Delete adjustment"
                      >
                        {deletingId === row.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={saving || Boolean(deletingId)}>
            Close
          </Button>
          {canCreate ? (
            <Button type="button" onClick={() => void save()} disabled={saving || Boolean(deletingId)}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
